import '@shared/ui/theme/global.css';

import { ApiError } from '@shared/api-client/index';
import { initI18n, metaT } from '@shared/ui/i18n/i18n.config';
import { useEvictCacheOnUserChange } from '@shared/ui/query/evict-on-user-change';
import { QueryProvider } from '@shared/ui/query/query-provider.component';
// Resolve to the same hashed asset the CSS @font-face points at, so the preload
// hits the real URL and the primary UI face doesn't wait on the CSS to parse.
import jakarta from '@shared/ui/theme/fonts/plus-jakarta-sans.woff2?url';
import { prePaintScript } from '@shared/ui/theme/theme';
import { ToastProvider } from '@shared/ui/toast/toast.component';
import { type ReactNode, useEffect } from 'react';
import { Links, Meta, Outlet, Scripts, ScrollRestoration } from 'react-router';

import type { Route } from './+types/root';
import en from './locales/en/common.json';
import { goToLogin } from './modules/core/auth';
import { QUERY_CACHE_KEY } from './modules/core/cache';
import { RouteProgress } from './modules/core/route-progress.component';
import { useMe } from './modules/core/session.hooks';
import { ShellSkeleton } from './modules/core/skeletons.component';
import { UpdateNudge } from './modules/core/update-nudge.component';

export { ErrorBoundary } from './modules/core/error-boundary.component';

// Bundle en so the first paint has copy (no key-flash before i18n loads).
initI18n({ en: { common: en } });

// Client-side rendered: there is no server loader. The shell-wide queries (the
// signed-in user, the workspace's projects) are fetched by their react-query
// hooks on mount; the sidebar shows placeholders until they resolve, and a 401
// bounces to the auth app via the QueryProvider error handler below. The theme is
// applied before first paint by prePaintScript (reads the shared .kiss.cat cookie).

// Default document title + description. A route's own meta() replaces this
// wholesale, so per-route titles still win; robots lives in <head> instead
// (per-route meta would otherwise drop it on routes that export their own).
export function meta(_args: Route.MetaArgs) {
	return [
		{ title: metaT('meta.root.title', 'KissCat Tasks') },
		{
			name: 'description',
			content: metaT('meta.root.description', 'Plan, track, and ship your work in KissCat Tasks.'),
		},
	];
}

// Brand (kiss.cat violet + pink accent) is defined per-theme in themes.css.

export function Layout({ children }: { children: ReactNode }) {
	// data-theme is set before first paint by prePaintScript (below), which reads the
	// shared .kiss.cat cookie / localStorage. suppressHydrationWarning: the script
	// refines the attribute before React hydrates, so the build-time default differs.
	return (
		<html lang="en" data-theme="light" suppressHydrationWarning>
			<head>
				<meta charSet="utf-8" />
				<meta name="app-version" content={__APP_VERSION__} />
				{/* interactive-widget=resizes-content: when the on-screen keyboard opens,
				    shrink the layout viewport (not just the visual one) so dvh-sized,
				    bottom-anchored UI — the create-task footer, the mobile quick-add — rides
				    above the keyboard instead of hiding behind it. Honoured by Android Chrome;
				    iOS Safari ignores it (the quick-add bar keeps its visualViewport lift). */}
				<meta
					name="viewport"
					content="width=device-width, initial-scale=1, interactive-widget=resizes-content"
				/>
				{/* Private authed app: keep every route out of search indexes. */}
				<meta name="robots" content="noindex, nofollow" />
				<link rel="icon" href="/favicon.svg" type="image/svg+xml" />
				<link rel="icon" href="/favicon-32.png" type="image/png" sizes="32x32" />
				<link rel="apple-touch-icon" href="/apple-touch-icon.png" />
				{/* Installable PWA: manifest + brand chrome colour + iOS home-screen meta. */}
				<link rel="manifest" href="/manifest.webmanifest" />
				<meta name="theme-color" content="#6a37ff" />
				<meta name="mobile-web-app-capable" content="yes" />
				<meta name="apple-mobile-web-app-capable" content="yes" />
				<meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
				<meta name="apple-mobile-web-app-title" content="Tasks" />
				{/* Preload the variable UI face (Plus Jakarta Sans)
				    so LCP text is not blocked on the stylesheet loading first. */}
				<link rel="preload" as="font" type="font/woff2" href={jakarta} crossOrigin="" />
				{/* Resolve the theme before first paint to avoid a flash. */}
				<script dangerouslySetInnerHTML={{ __html: prePaintScript }} />
				<Meta />
				<Links />
			</head>
			<body>
				{children}
				<ScrollRestoration />
				<Scripts />
			</body>
		</html>
	);
}

/**
 * Drops a stale persisted cache when a different account becomes active without an
 * in-app clear — e.g. add-account, which lands here via a redirect from the auth app
 * and would otherwise rehydrate the previous user's data. Renders nothing.
 */
function SessionCacheGuard() {
	const { data: me } = useMe();
	useEvictCacheOnUserChange(me?.user.id, QUERY_CACHE_KEY);
	return null;
}

export default function App() {
	// Mark the document hydrated once the client has mounted — a signal e2e tests
	// wait on before interacting, so they never click a not-yet-hydrated control.
	useEffect(() => {
		document.documentElement.dataset.hydrated = 'true';
	}, []);

	return (
		<QueryProvider
			persistKey={QUERY_CACHE_KEY}
			buster={__BUILD_ID__}
			onError={(error) => {
				// A 401 from any query/mutation means the session lapsed mid-use —
				// bounce to the auth app (client-side only). useMe swallows its own
				// 401 (returns null), so this never loops on the session check.
				if (typeof window !== 'undefined' && error instanceof ApiError && error.status === 401) {
					goToLogin();
					return;
				}
				console.error(error);
			}}
		>
			<ToastProvider>
				<SessionCacheGuard />
				<RouteProgress />
				<Outlet />
				{/* Long-lived-tab freshness nudge; client-only, renders null at rest. */}
				<UpdateNudge />
			</ToastProvider>
		</QueryProvider>
	);
}

// Shown while the client bundle boots and hydrates (SPA mode prerenders this into
// index.html). A shaped skeleton of the whole shell — so the first paint is the app
// taking form, not a spinner or blank. Only the very first load hits this; client
// navigations never do, and repeat visits paint real data from the persisted cache.
export function HydrateFallback() {
	return <ShellSkeleton />;
}
